import { QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'jotai';
import { lazy, Suspense } from 'react';
import { RouterProvider } from 'react-aria-components';
import { ErrorBoundary } from 'react-error-boundary';
import { Route, Switch, useLocation } from 'wouter';
import ErrorPage from './components/ErrorPage';
import NotFoundPage from './components/NotFoundPage';
import Toaster from './components/Toaster';
import BaseLayout from './layouts/BaseLayout';
import { queryClient } from './lib/request';
import Authorization from './modules/auth/Authorization';
import useAuthorization from './modules/auth/useAuthorization';
import AuthFailed from './routes/authfailed';
import DataManagement from './routes/data-management';
import AccountDeletion from './routes/data-management/account-deletion';
import Earning from './routes/earning';
import Home from './routes/home';
import Interface from './routes/interface';
import Profile from './routes/profile';
import SubscriptionCancel from './routes/subscription/cancel';
import Subscription from './routes/subscription/home';
import SubscriptionTransactions from './routes/subscription/transactions';
import SubscriptionUpgrade from './routes/subscription/upgrade';
import Support from './routes/support';
import { store } from './store';

import 'react-loading-skeleton/dist/skeleton.css';

const DevAppearanceSwitcher = import.meta.env.DEV
  ? lazy(() => import('./components/DevAppearanceSwitcher'))
  : null;

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  useAuthorization();

  return (
    <Authorization>
      <BaseLayout>
        <Component />
      </BaseLayout>
    </Authorization>
  );
}

function App() {
  const [, navigate] = useLocation();

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider navigate={navigate}>
          <ErrorBoundary FallbackComponent={ErrorPage}>
            <Switch>
              <Route path="/support">
                <BaseLayout>
                  <Route component={Support} />
                </BaseLayout>
              </Route>
              <Route path="/">
                <ProtectedRoute component={Home} />
              </Route>
              <Route path="/profile">
                <ProtectedRoute component={Profile} />
              </Route>
              <Route path="/account">
                <ProtectedRoute component={Home} />
              </Route>
              <Route path="/interface">
                <ProtectedRoute component={Interface} />
              </Route>
              <Route path="/data-management">
                <ProtectedRoute component={DataManagement} />
              </Route>
              <Route path="/data-management/account-deletion">
                <ProtectedRoute component={AccountDeletion} />
              </Route>
              <Route path="/subscription">
                <ProtectedRoute component={Subscription} />
              </Route>
              <Route path="/subscription/upgrade">
                <ProtectedRoute component={SubscriptionUpgrade} />
              </Route>
              <Route path="/subscription/cancel">
                <ProtectedRoute component={SubscriptionCancel} />
              </Route>
              <Route path="/subscription/transactions">
                <ProtectedRoute component={SubscriptionTransactions} />
              </Route>
              <Route path="/earning">
                <ProtectedRoute component={Earning} />
              </Route>
              <Route path="/auth-failed">
                <BaseLayout>
                  <Route component={AuthFailed} />
                </BaseLayout>
              </Route>
              <Route component={NotFoundPage} />
            </Switch>
            <Toaster />
          </ErrorBoundary>
          {DevAppearanceSwitcher && (
            <Suspense fallback={null}>
              <DevAppearanceSwitcher />
            </Suspense>
          )}
        </RouterProvider>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
